<script setup lang="ts">
import './assets/style.scss'
import { useModel } from './../model/model.ts'
import { VSelectSearch } from '@/shared/ui'
import { computed, type PropType } from 'vue'
import { sortBy } from 'lodash'
import { useGroupMain } from '@/entities/Group/Main'

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  hideDetails: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  isSystem: {
    type: [Boolean, Number, String] as PropType<boolean | number | string | null>,
    default: null,
  },
  /** When false, do not intersect options with group.object_types (e.g. admin manuals create). */
  restrictToGroupObjectTypes: {
    type: Boolean,
    default: true,
  },
  /** Optional list of allowed type ids to further restrict the dropdown. */
  allowedIds: {
    type: Array as PropType<number[]>,
    default: null,
  },
})

const { items, loadData } = useModel()
const loadQuery = loadData()
const { isLoading, isFetching, isFetched } = loadQuery

const model = defineModel()

const groupMain = useGroupMain()

const allowedObjectTypeIds = computed<number[] | null>(() => groupMain.group?.object_types ?? null)

const normalizeIsSystem = (value: unknown): boolean | null => {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1

  const normalized = String(value).toLowerCase()
  if (normalized === '1' || normalized === 'true') return true
  if (normalized === '0' || normalized === 'false') return false

  return Boolean(value)
}

const selectedIds = computed<number[]>(() => {
  if (Array.isArray(model.value)) {
    return model.value
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
  }

  const id = Number(model.value)
  return Number.isFinite(id) ? [id] : []
})

/** VSelect `item-title="name"` — some API rows only expose `object_name`. */
function rowWithDisplayName(raw: unknown) {
  const it = raw as Record<string, unknown>
  const nameStr = typeof it.name === 'string' ? it.name.trim() : ''
  const objectNameStr = typeof it.object_name === 'string' ? it.object_name.trim() : ''
  const titleStr = typeof it.title === 'string' ? it.title.trim() : ''
  const label = nameStr || objectNameStr || titleStr
  if (!label) return { ...it, name: String(it.id ?? '') }
  if (nameStr) return raw
  return { ...it, name: label }
}

const itemsForSelect = computed(() => items.value.map((row) => rowWithDisplayName(row)))

/** Until the scoped object-types query settles, keep the select in a loading state (avoids blank label). */
const listUiBusy = computed(
  () =>
    isLoading.value ||
    isFetching.value ||
    (!isFetched.value && items.value.length === 0),
)

/* Фильтруем типы в зависимости картографический элемент/справочник и доступности для группы */
const list = computed(() => {
  const normalizedIsSystem = normalizeIsSystem(props.isSystem)
  const baseList =
    normalizedIsSystem === null
      ? itemsForSelect.value
      : itemsForSelect.value.filter((item) => normalizeIsSystem(item.is_system) === normalizedIsSystem)

  const allowedIds = allowedObjectTypeIds.value

  if (!props.restrictToGroupObjectTypes) {
    return sortBy(baseList, 'name')
  }

  // Если для группы нет ограничений по object_types — возвращаем весь базовый список
  if (!Array.isArray(allowedIds) || allowedIds.length === 0) {
    return sortBy(baseList, 'name')
  }

  // Оставляем разрешенные типы, но не скрываем уже выбранные (иначе VSelect показывает только id)
  const selectedSet = new Set(selectedIds.value)
  const filteredByGroup = baseList.filter((item) => {
    const itemId = Number((item as any).id)
    return allowedIds.includes(itemId) || selectedSet.has(itemId)
  })

  const result = sortBy(filteredByGroup, 'name')

  if (props.allowedIds != null && Array.isArray(props.allowedIds)) {
    const allowedSet = new Set(props.allowedIds)
    return result.filter((item) => {
      const itemId = Number((item as any).id)
      return allowedSet.has(itemId) || selectedSet.has(itemId)
    })
  }

  return result
})
</script>

<template>
  <VSelectSearch
    :label
    :placeholder
    :loading="listUiBusy"
    :items="list"
    :disabled
    :hide-details
    item-title="name"
    item-value="id"
    :multiple
    v-model="model"
  />
</template>
